/**
 * Date Utility Service - Shared date conversion utilities
 * 
 * This service provides robust date parsing and conversion methods
 * that handle various date formats and prevent timezone-related issues
 * that can cause off-by-one day errors in form workflows.
 * 
 * IMPORTANT: Workflow Date Handling (SignedDate, AssignedDate)
 * ============================================================
 * The workflow fields SignedDate and AssignedDate are PURE DATES (calendar dates only).
 * They are stored in the database in yyyy-MM-dd format without any time component.
 * 
 * These dates should NEVER be converted through timezone adjustments as they represent
 * calendar dates, not timestamps.
 * 
 * Use the dedicated methods:
 * - parseWorkflowDate(): Convert from any format to yyyy-MM-dd for API submission
 * - formatWorkflowDateForDisplay(): Convert yyyy-MM-dd to user's preferred display format
 * 
 * DO NOT use convertUtcToLocalIso() for workflow dates as it applies timezone conversion.
 * 
 * Updated Components:
 * - editform.component.ts: saveIssue(), FncSign(), GetValues()
 * - addform.component.ts: saveIssue(), FncSign()
 * 
 * @version 2.0.0 - Added workflow date handling methods
 */

import { Injectable } from '@angular/core';
import { ConfigStateService } from '@abp/ng.core';
import { IdentityUserService } from '@abp/ng.identity/proxy';
import { firstValueFrom } from 'rxjs';
import { UserCacheService } from './user-cache.service';

@Injectable({
  providedIn: 'root'
})
export class DateUtilService {
  private dateFormat: string = '';

  constructor(
    private config: ConfigStateService,
    private userService: IdentityUserService,
    private userCacheService: UserCacheService
  ) {}

  /**
   * Robust date conversion method that prevents timezone-related date shifts
   * 
   * @param utcDateInput - Date input as string or Date object
   * @param userDateFormat - Optional date format, will fetch from user settings if not provided
   * @returns Promise<string> - Date in YYYY-MM-DD format
   */
  async convertUtcToLocalIso(utcDateInput: string | Date, userDateFormat?: string): Promise<string> {
    
    if (utcDateInput == null || utcDateInput == '') {
      return '';
    }
    
    // Use provided format or ensure we have the user's date format
    const dateFormat = userDateFormat || await this.ensureDateFormat();
    
    const utcDateString = String(utcDateInput).trim();
    let parsedDate: Date | null = null;
  
    // Try default parsing first
    const isoParsed = new Date(utcDateString);
    if (!isNaN(isoParsed.getTime())) {
      parsedDate = isoParsed;
    }
  
    // If not parsed correctly or not explicitly UTC, try custom formats based on user's DateFormat
    if (!parsedDate || !utcDateString.endsWith('Z')) {
      const formats = this.getDateFormats(dateFormat);
      
      // Try each format until one works
      for (const format of formats) {
        const match = utcDateString.match(format.regex);
        if (match) {
          try {
            const candidate = format.parse(match);
            if (!isNaN(candidate.getTime())) {
              parsedDate = candidate;
              break;
            }
          } catch (e) {
            console.warn('Date parsing error with format', format, e);
          }
        }
      }
    }
  
    if (!parsedDate || isNaN(parsedDate.getTime())) {
      console.warn('Could not parse date:', utcDateString);
      return ''; // Return empty string instead of original to avoid errors
    }
  
    // FIX: Prevent timezone shift by creating a UTC date string manually
    // This ensures the date component remains the same as what the user selected
    const year = parsedDate.getFullYear();
    const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
    const day = String(parsedDate.getDate()).padStart(2, '0');
    
    // Return the date in YYYY-MM-DD format without converting to ISO string
    // which would apply timezone adjustments
    const result = `${year}-${month}-${day}`;
    
    return result;
  }

  /**
   * Get the user's preferred date format, with caching
   * @returns Promise<string> - Date format string
   */
  private async ensureDateFormat(): Promise<string> {
    if (this.dateFormat) return this.dateFormat; // Already cached
    
    try {
      const config = this.config.getAll();
      const userId = config.currentUser.id;
      
      // Ensure cache is loaded first
      if (!this.userCacheService.isReady()) {
        await this.userCacheService.refresh();
      }
      
      // Get from cache (should be available after refresh)
      let userData = this.userCacheService.getUserById(userId);
      const formatCode = userData?.extraProperties?.DateFormat;
      
      switch(formatCode) {
        case 0: this.dateFormat = 'dd/MM/yyyy'; break;
        case 1: this.dateFormat = 'MM/dd/yyyy'; break;
        case 2: this.dateFormat = 'yyyy/dd/MM'; break;
        case 3: this.dateFormat = 'yyyy/MM/dd'; break;
        default: this.dateFormat = 'dd/MM/yyyy'; break;
      }
      
      return this.dateFormat;
    } catch (error) {
      console.error('Error ensuring date format:', error);
      this.dateFormat = 'dd/MM/yyyy'; // Default format as fallback
      return this.dateFormat;
    }
  }

  /**
   * Generate date format parsers based on user's preferred date format
   * @param dateFormat - User's preferred date format
   * @returns Array of format parsers
   */
  private getDateFormats(dateFormat: string) {
    const formats = [];
    
    // Add format matchers according to the user's preferred date format
    if (dateFormat === 'MM/dd/yyyy') {
      // US format: MM/DD/YYYY
      formats.push({
        regex: /^(\d{2})\/(\d{2})\/(\d{4})(?: (\d{1,2}):(\d{2}):(\d{2}) ([AP]M))?$/,
        parse: (m: RegExpMatchArray) => {
          const month = parseInt(m[1], 10) - 1; // Month is 0-based in JS Date
          const day = parseInt(m[2], 10);
          const year = parseInt(m[3], 10);
          let hours = 0, minutes = 0, seconds = 0;

          if (m[4]) {
            hours = parseInt(m[4], 10);
            minutes = parseInt(m[5], 10);
            seconds = parseInt(m[6], 10);
            const period = m[7];
            if (period === 'PM' && hours !== 12) hours += 12;
            if (period === 'AM' && hours === 12) hours = 0;
          }

          return new Date(year, month, day, hours, minutes, seconds);
        }
      });
      
      formats.push({
        // MM-DD-YYYY - US format
        regex: /^(\d{2})-(\d{2})-(\d{4})$/,
        parse: (m: RegExpMatchArray) => new Date(parseInt(m[3], 10), parseInt(m[1], 10) - 1, parseInt(m[2], 10))
      });
    } 
    else if (dateFormat === 'dd/MM/yyyy') {
      // European format: DD/MM/YYYY
      formats.push({
        regex: /^(\d{2})\/(\d{2})\/(\d{4})(?: (\d{1,2}):(\d{2}):(\d{2}) ([AP]M))?$/,
        parse: (m: RegExpMatchArray) => {
          const day = parseInt(m[1], 10);
          const month = parseInt(m[2], 10) - 1; // Month is 0-based in JS Date
          const year = parseInt(m[3], 10);
          let hours = 0, minutes = 0, seconds = 0;

          if (m[4]) {
            hours = parseInt(m[4], 10);
            minutes = parseInt(m[5], 10);
            seconds = parseInt(m[6], 10);
            const period = m[7];
            if (period === 'PM' && hours !== 12) hours += 12;
            if (period === 'AM' && hours === 12) hours = 0;
          }

          return new Date(year, month, day, hours, minutes, seconds);
        }
      });
      
      formats.push({
        // DD-MM-YYYY - European format
        regex: /^(\d{2})-(\d{2})-(\d{4})$/,
        parse: (m: RegExpMatchArray) => new Date(parseInt(m[3], 10), parseInt(m[2], 10) - 1, parseInt(m[1], 10))
      });
    }
    else if (dateFormat === 'yyyy/dd/MM') {
      // Year first format: YYYY/DD/MM
      formats.push({
        regex: /^(\d{4})\/(\d{2})\/(\d{2})(?: (\d{1,2}):(\d{2}):(\d{2}) ([AP]M))?$/,
        parse: (m: RegExpMatchArray) => {
          const year = parseInt(m[1], 10);
          const day = parseInt(m[2], 10);
          const month = parseInt(m[3], 10) - 1; // Month is 0-based in JS Date
          let hours = 0, minutes = 0, seconds = 0;

          if (m[4]) {
            hours = parseInt(m[4], 10);
            minutes = parseInt(m[5], 10);
            seconds = parseInt(m[6], 10);
            const period = m[7];
            if (period === 'PM' && hours !== 12) hours += 12;
            if (period === 'AM' && hours === 12) hours = 0;
          }

          return new Date(year, month, day, hours, minutes, seconds);
        }
      });
      
      formats.push({
        // YYYY-DD-MM format
        regex: /^(\d{4})-(\d{2})-(\d{2})$/,
        parse: (m: RegExpMatchArray) => new Date(parseInt(m[1], 10), parseInt(m[3], 10) - 1, parseInt(m[2], 10))
      });
    }
    else if (dateFormat === 'yyyy/MM/dd') {
      // ISO-like format: YYYY/MM/DD
      formats.push({
        regex: /^(\d{4})\/(\d{2})\/(\d{2})(?: (\d{1,2}):(\d{2}):(\d{2}) ([AP]M))?$/,
        parse: (m: RegExpMatchArray) => {
          const year = parseInt(m[1], 10);
          const month = parseInt(m[2], 10) - 1; // Month is 0-based in JS Date
          const day = parseInt(m[3], 10);
          let hours = 0, minutes = 0, seconds = 0;

          if (m[4]) {
            hours = parseInt(m[4], 10);
            minutes = parseInt(m[5], 10);
            seconds = parseInt(m[6], 10);
            const period = m[7];
            if (period === 'PM' && hours !== 12) hours += 12;
            if (period === 'AM' && hours === 12) hours = 0;
          }

          return new Date(year, month, day, hours, minutes, seconds);
        }
      });
      
      formats.push({
        // ISO format YYYY-MM-DD
        regex: /^(\d{4})-(\d{2})-(\d{2})$/,
        parse: (m: RegExpMatchArray) => new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10))
      });
    }
    
    // Add generic fallback formats that work regardless of user's date format
    
    // ISO format with timezone
    formats.push({
      regex: /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z$/,
      parse: (utcDateString: string) => new Date(utcDateString)
    });
    
    // Full date string with timezone: Tue Apr 08 2025 00:00:00 GMT+0530 (India Standard Time)
    formats.push({
      regex: /^[A-Za-z]{3} [A-Za-z]{3} \d{2} \d{4} \d{2}:\d{2}:\d{2} GMT.*$/,
      parse: (utcDateString: string) => new Date(utcDateString)
    });
    
    // Smart format detection as a last resort
    formats.push({
      regex: /^(\d{1,4})[-/](\d{1,2})[-/](\d{1,4})$/,
      parse: (m: RegExpMatchArray) => {
        // Determine format based on values
        const part1 = parseInt(m[1], 10);
        const part2 = parseInt(m[2], 10);
        const part3 = parseInt(m[3], 10);
        
        // If part1 > 31, it must be a year
        if (part1 > 31) {
          // YYYY-MM-DD or YYYY/MM/DD
          if (part2 <= 12) {
            return new Date(part1, part2 - 1, part3);
          } else {
            // YYYY-DD-MM or YYYY/DD/MM
            return new Date(part1, part3 - 1, part2);
          }
        } 
        // If part3 > 31, it must be a year
        else if (part3 > 31) {
          // MM-DD-YYYY or MM/DD/YYYY or DD-MM-YYYY or DD/MM/YYYY
          if (part1 <= 12 && part2 <= 31) {
            // Assume MM-DD-YYYY or MM/DD/YYYY (US format)
            return new Date(part3, part1 - 1, part2);
          } else if (part1 <= 31 && part2 <= 12) {
            // Assume DD-MM-YYYY or DD/MM/YYYY (European format)
            return new Date(part3, part2 - 1, part1);
          }
        }
        
        // Default to the user's preferred date format
        if (dateFormat === 'MM/dd/yyyy') {
          return new Date(part3, part1 - 1, part2);
        } else if (dateFormat === 'dd/MM/yyyy') {
          return new Date(part3, part2 - 1, part1);
        } else if (dateFormat === 'yyyy/dd/MM') {
          return new Date(part1, part3 - 1, part2);
        } else {
          return new Date(part1, part2 - 1, part3); // yyyy/MM/dd
        }
      }
    });

    return formats;
  }

  /**
   * Parse workflow date (SignedDate, AssignedDate) which are stored as pure dates (yyyy-MM-dd)
   * These dates should NOT go through timezone conversion as they represent calendar dates only
   * 
   * CRITICAL: This method handles Date objects from datepickers which are in LOCAL time.
   * We must use getFullYear(), getMonth(), getDate() (LOCAL methods) NOT UTC methods
   * to preserve the calendar date that the user selected.
   * 
   * @param workflowDate - Date value from backend (yyyy-MM-dd string or Date object)
   * @returns string - Date in yyyy-MM-dd format, or empty string if null/invalid
   */
  parseWorkflowDate(workflowDate: string | Date | null | undefined): string {
    if (!workflowDate) {
      return '';
    }

    // Handle Date object directly (from datepicker) - use LOCAL time methods
    if (workflowDate instanceof Date) {
      if (!isNaN(workflowDate.getTime())) {
        // IMPORTANT: Use LOCAL time methods (getFullYear, getMonth, getDate)
        // The datepicker gives us a Date object at midnight LOCAL time
        // We want to preserve that exact calendar date
        const year = workflowDate.getFullYear();
        const month = String(workflowDate.getMonth() + 1).padStart(2, '0');
        const day = String(workflowDate.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
      return '';
    }

    const dateStr = String(workflowDate).trim();
    
    // Already in correct format (yyyy-MM-dd)
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return dateStr;
    }

    // Handle ISO timestamp format (e.g., 2025-10-13T00:00:00Z) - extract just date portion
    const isoMatch = dateStr.match(/^(\d{4}-\d{2}-\d{2})(?:T|$)/);
    if (isoMatch) {
      return isoMatch[1]; // Return just the yyyy-MM-dd portion
    }

    // Handle other date string formats
    try {
      const parsed = new Date(dateStr);
      if (!isNaN(parsed.getTime())) {
        // For string-based dates, check if it's UTC (ends with Z) or local
        if (dateStr.includes('T') && dateStr.endsWith('Z')) {
          // UTC timestamp - use UTC methods
          const year = parsed.getUTCFullYear();
          const month = String(parsed.getUTCMonth() + 1).padStart(2, '0');
          const day = String(parsed.getUTCDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        } else {
          // Local date string - use local methods
          const year = parsed.getFullYear();
          const month = String(parsed.getMonth() + 1).padStart(2, '0');
          const day = String(parsed.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        }
      }
    } catch (e) {
      console.warn('Unable to parse workflow date:', dateStr, e);
    }

    return '';
  }

  /**
   * Parse a workflow date (SignedDate, AssignedDate) coming from the backend into a
   * Date object pinned to LOCAL midnight of the same calendar day.
   *
   * The backend serializes these pure-date values with a UTC marker (e.g.
   * "2025-06-30T00:00:00Z") because AbpClockOptions.Kind is Utc. Passing that string
   * straight into `new Date()` interprets it as UTC midnight, which rolls back to the
   * previous calendar day in negative-offset timezones. On the next save
   * parseWorkflowDate() reads the local day and the value loses a day on every edit.
   *
   * By extracting only the yyyy-MM-dd portion (via parseWorkflowDate) and rebuilding the
   * Date from local components, load becomes symmetric with save and the calendar date is
   * preserved regardless of the user's timezone.
   *
   * @param workflowDate - Date value from backend (ISO timestamp, yyyy-MM-dd, or Date)
   * @returns Date at local midnight for the calendar date, or null if empty/invalid
   */
  parseWorkflowDateToLocalDate(workflowDate: string | Date | null | undefined): Date | null {
    const dateOnly = this.parseWorkflowDate(workflowDate);
    if (!dateOnly) {
      return null;
    }

    const [year, month, day] = dateOnly.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  /**
   * Format workflow date (SignedDate, AssignedDate) for display in the UI
   * Converts yyyy-MM-dd to the user's preferred date format
   * 
   * @param workflowDate - Date in yyyy-MM-dd format from backend (or ISO timestamp)
   * @param userDateFormat - Optional date format, will fetch from user settings if not provided
   * @returns Promise<string> - Formatted date string for display
   */
  async formatWorkflowDateForDisplay(
    workflowDate: string | Date | null | undefined,
    userDateFormat?: string
  ): Promise<string> {
    if (!workflowDate) {
      return '';
    }

    const dateFormat = userDateFormat || await this.ensureDateFormat();
    const normalized = this.parseWorkflowDate(workflowDate);

    if (!normalized) {
      console.warn('Invalid workflow date format, expected yyyy-MM-dd:', workflowDate);
      return typeof workflowDate === 'string' ? workflowDate : '';
    }

    return this.formatIsoDate(normalized, dateFormat);
  }

  async formatPureDateForDisplay(
    dateInput: string | Date | null | undefined,
    userDateFormat?: string
  ): Promise<string> {
    const dateFormat = userDateFormat || await this.ensureDateFormat();
    return this.formatPureDateForDisplaySync(dateInput, dateFormat);
  }

  formatPureDateForDisplaySync(
    dateInput: string | Date | null | undefined,
    dateFormat?: string
  ): string {
    if (dateInput === null || dateInput === undefined || dateInput === '') {
      return '';
    }

    const effectiveFormat = (dateFormat && dateFormat.trim()) || this.dateFormat || 'dd/MM/yyyy';
    const normalized = this.parseWorkflowDate(dateInput);

    if (!normalized) {
      if (typeof dateInput !== 'string') {
        return '';
      }

      const dateStr = dateInput.trim();
      const formats = this.getDateFormats(effectiveFormat);
      for (const format of formats) {
        const match = dateStr.match(format.regex);
        if (!match) {
          continue;
        }

        try {
          const parsed = format.parse(match);
          if (!isNaN(parsed.getTime())) {
            const year = parsed.getFullYear();
            const month = String(parsed.getMonth() + 1).padStart(2, '0');
            const day = String(parsed.getDate()).padStart(2, '0');
            const parsedIso = `${year}-${month}-${day}`;
            return this.formatIsoDate(parsedIso, effectiveFormat);
          }
        } catch (e) {
          console.warn('Date parsing error with format', format, e);
        }
      }

      return dateInput;
    }

    return this.formatIsoDate(normalized, effectiveFormat);
  }

  async getUserDateFormat(): Promise<string> {
    return this.ensureDateFormat();
  }

  private formatIsoDate(isoDate: string, dateFormat: string): string {
    const match = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) {
      return isoDate;
    }

    const [, year, month, day] = match;
    const normalizedFormat = (dateFormat || '').toLowerCase();

    switch (normalizedFormat) {
      case 'mm/dd/yyyy':
        return `${month}/${day}/${year}`;
      case 'yyyy/dd/mm':
        return `${year}/${day}/${month}`;
      case 'yyyy/mm/dd':
        return `${year}/${month}/${day}`;
      case 'dd/mm/yyyy':
      default:
        return `${day}/${month}/${year}`;
    }
  }

  /**
   * Clear the cached date format (useful for testing or when user changes preferences)
   */
  clearDateFormatCache(): void {
    this.dateFormat = '';
  }
}
